import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { apiFetch } from '../bizsuite-app';
import SearchableSelect from '../components/SearchableSelect';

/**
 * Per-availability dot colour map. The table the user wants displayed in
 * the Color Code legend; also drives the dot painted next to each row.
 *
 *   Green  — actively available
 *   Blue   — signed in but not available right now (idle, away, BRB, DND)
 *   Red    — actively busy / on a call / in a meeting
 *   Amber  — busy AND idle (likely missed a meeting)
 *   Grey   — Offline (not signed in)
 *   White  — Graph couldn't determine (licence/permission issue)
 */
const COLOR_CODE = [
  { value: 'Available',        meaning: 'Signed in, not in a meeting, no Do-Not-Disturb on', dot: '#639922', label: 'Green' },
  { value: 'AvailableIdle',    meaning: 'Available but no keyboard/mouse activity for ~5 min', dot: '#378ADD', label: 'Blue' },
  { value: 'Busy',             meaning: 'Manually set, or in a Teams call/meeting',           dot: '#E24B4A', label: 'Red' },
  { value: 'BusyIdle',         meaning: 'Busy + idle',                                        dot: '#EF9F27', label: 'Amber' },
  { value: 'DoNotDisturb',     meaning: 'Manual DND, Focus mode, or presenting',              dot: '#378ADD', label: 'Blue' },
  { value: 'BeRightBack',      meaning: 'Manually set "BRB"',                                 dot: '#378ADD', label: 'Blue' },
  { value: 'Away',             meaning: 'Auto-away after ~5 min idle, or manual',             dot: '#378ADD', label: 'Blue' },
  { value: 'Offline',          meaning: 'Not signed into any Microsoft 365 service',          dot: '#888780', label: 'Grey' },
  { value: 'PresenceUnknown',  meaning: "Graph couldn't determine (e.g. licence issue)",      dot: '#FFFFFF', label: 'White' },
];
const AVAIL_TO_DOT = COLOR_CODE.reduce((m, r) => { m[r.value] = r.dot; return m; }, {});

/**
 * WorkforceLive — Option A: enhanced Employees Active table with live
 * status + location + leave/WFH/roster compliance from Microsoft Graph.
 *
 * Tracking is opt-in at the Group Tag level (see GroupTags.jsx → Monitor
 * checkbox). Only employees in a Monitor=true tag appear here.
 *
 * Polls /api/workforce/live every 60 seconds while open. Filter chips
 * across the top, city dropdown on the right, sortable columns.
 */

// Filter chips split into TWO independent groups — Teams presence (sourced
// from Microsoft Graph) and BizSuite-side attendance (leave / WFH / roster).
// Selections within each group are multi-select; selections across groups
// AND together (e.g. Available + WFH = "available people working from home").
// Empty selection = no filter applied for that group.
const TEAMS_FILTERS = [
  { key: 'available', label: 'Available', color: 'rgba(99,153,34,0.15)',   text: '#9DC559', border: 'rgba(99,153,34,0.40)' },
  { key: 'busy',      label: 'Busy',      color: 'rgba(226,75,75,0.15)',   text: '#F09595', border: 'rgba(226,75,75,0.40)' },
  { key: 'offline',   label: 'Offline',   color: 'rgba(255,255,255,0.06)', text: '#94A3B8', border: 'rgba(255,255,255,0.10)' },
];
const ATTENDANCE_FILTERS = [
  { key: 'leave',        label: 'On leave',  color: 'rgba(245,158,11,0.15)', text: '#FCD34D', border: 'rgba(245,158,11,0.40)' },
  { key: 'wfh',          label: 'WFH',       color: 'rgba(55,138,221,0.15)', text: '#85B7EB', border: 'rgba(55,138,221,0.40)' },
  { key: 'noncompliant', label: 'Off roster', color: 'rgba(244,63,94,0.10)', text: '#FB7185', border: 'rgba(244,63,94,0.40)' },
];

// Map Graph availability → bucket the filter chips care about
function bucketize(emp) {
  if (emp.on_leave) return 'leave';
  if (emp.on_wfh)   return 'wfh';
  const a = (emp.presence_status || '').toLowerCase();
  if (a.includes('available')) return 'available';
  if (a.includes('busy') || a.includes('donotdisturb') || a.includes('inacall') || a.includes('inameeting')) return 'busy';
  if (a.includes('offline') || a.includes('unknown') || a === 'notmonitored' || a === '') return 'offline';
  return 'available';
}

export default function WorkforceLive() {
  const [rows, setRows] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Two independent filter groups:
  //   teamsSel       — which Teams presence buckets are accepted (Set of keys)
  //   attendanceSel  — which BizSuite attendance flags are accepted (Set)
  // Empty Set = no filter for that group (show all). The two AND together.
  const [teamsSel, setTeamsSel] = useState(() => new Set());
  const [attendanceSel, setAttendanceSel] = useState(() => new Set());
  const [cityFilter, setCityFilter] = useState('');
  const [search, setSearch] = useState('');
  // Color Code legend modal — opens from the small button next to Refresh now.
  const [legendOpen, setLegendOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await apiFetch('/api/workforce/live');
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d.error || `HTTP ${r.status}`); setRows([]); }
      else { setError(null); setRows(d.employees || []); setFetchedAt(d.fetched_at); }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll every 60s while the page is open. Visibility check skips the call
  // when the tab is in the background to save Graph quota.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, 60_000);
    return () => clearInterval(id);
  }, [load]);

  // Cities — derive from data so the dropdown always reflects what's there.
  const cities = useMemo(() => {
    const set = new Set();
    rows.forEach(r => { if (r.last_signin_city) set.add(r.last_signin_city); });
    return Array.from(set).sort();
  }, [rows]);

  // Counts for the chip badges. Each chip's number reflects rows that match
  // THAT specific bucket — independent of any other filters (so the user can
  // see "if I clicked WFH, I'd narrow to N rows" without doing it).
  const counts = useMemo(() => {
    const out = { available: 0, busy: 0, offline: 0, leave: 0, wfh: 0, noncompliant: 0 };
    for (const r of rows) {
      // Teams bucket — purely availability-driven (ignores leave/WFH overlay)
      const a = (r.presence_status || '').toLowerCase();
      if (a.includes('available')) out.available++;
      else if (a.includes('busy') || a.includes('donotdisturb') || a.includes('inacall') || a.includes('inameeting') || a.includes('presenting')) out.busy++;
      else out.offline++;     // away / offline / unknown
      // Attendance flags
      if (r.on_leave) out.leave++;
      if (r.on_wfh)   out.wfh++;
      if (r.roster_status === 'non-compliant') out.noncompliant++;
    }
    return out;
  }, [rows]);

  // Helper for matching a single row against a chip key
  const matchesTeams = (r, key) => {
    const a = (r.presence_status || '').toLowerCase();
    if (key === 'available') return a.includes('available');
    if (key === 'busy')      return a.includes('busy') || a.includes('donotdisturb') || a.includes('inacall') || a.includes('inameeting') || a.includes('presenting');
    if (key === 'offline')   return !a || a.includes('offline') || a.includes('away') || a.includes('unknown') || a.includes('berightback');
    return false;
  };
  const matchesAttendance = (r, key) => {
    if (key === 'leave')        return !!r.on_leave;
    if (key === 'wfh')          return !!r.on_wfh;
    if (key === 'noncompliant') return r.roster_status === 'non-compliant';
    return false;
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      // Teams group — empty Set means "no filter"; otherwise OR within group
      if (teamsSel.size && !Array.from(teamsSel).some(k => matchesTeams(r, k))) return false;
      // Attendance group — independent OR within group
      if (attendanceSel.size && !Array.from(attendanceSel).some(k => matchesAttendance(r, k))) return false;
      if (cityFilter && r.last_signin_city !== cityFilter) return false;
      if (q) {
        const blob = [r.full_name, r.designation, r.dept_name, r.office_name, r.group_tag_name].filter(Boolean).join(' ').toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [rows, teamsSel, attendanceSel, cityFilter, search]);

  // Chip toggle helpers
  const toggleTeams      = k => setTeamsSel(s => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const toggleAttendance = k => setAttendanceSel(s => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const clearAllFilters  = () => { setTeamsSel(new Set()); setAttendanceSel(new Set()); setCityFilter(''); setSearch(''); };
  const anyFilterActive  = teamsSel.size > 0 || attendanceSel.size > 0 || !!cityFilter || !!search.trim();

  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 12, color: '#94A3B8' }}>
            {fetchedAt ? `Updated ${new Date(fetchedAt).toLocaleTimeString()} · auto-refresh 60s` : 'Loading…'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setLegendOpen(true)} style={btnLegend} title="Show colour legend">
            <span style={{ display: 'inline-flex', gap: 3, marginRight: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: '#639922' }} />
              <span style={{ width: 8, height: 8, borderRadius: 999, background: '#378ADD' }} />
              <span style={{ width: 8, height: 8, borderRadius: 999, background: '#E24B4A' }} />
            </span>
            Color Code
          </button>
          <button onClick={load} style={btnSecondary}>Refresh now</button>
        </div>
      </div>

      {legendOpen && <ColorCodeModal onClose={() => setLegendOpen(false)} />}

      {error && (
        <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.40)', color: '#FB7185', fontSize: 12.5, marginBottom: 14 }}>
          {error}
        </div>
      )}

      {/* Filter strip — TWO independent groups stacked, plus a search/city
          row underneath. Within each group, multiple chips are togglable;
          empty selection means "no filter for that group". The two groups
          AND together (e.g. Available + WFH = available WFH employees). */}
      <div style={{ marginBottom: 12, padding: '12px 14px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10 }}>
        {/* Group 1 — Teams presence */}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          <span style={groupLabel}>Teams status</span>
          {TEAMS_FILTERS.map(f => {
            const on = teamsSel.has(f.key);
            return (
              <button key={f.key} onClick={() => toggleTeams(f.key)} style={chipStyle(on, f)}>
                ● {f.label} · {counts[f.key] ?? 0}
              </button>
            );
          })}
        </div>
        {/* Group 2 — BizSuite attendance */}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <span style={groupLabel}>Attendance</span>
          {ATTENDANCE_FILTERS.map(f => {
            const on = attendanceSel.has(f.key);
            return (
              <button key={f.key} onClick={() => toggleAttendance(f.key)} style={chipStyle(on, f)}>
                ● {f.label} · {counts[f.key] ?? 0}
              </button>
            );
          })}
          {anyFilterActive && (
            <button onClick={clearAllFilters} style={{ ...chipStyle(false, { border: 'rgba(255,255,255,0.10)' }), color: '#FB7185', borderColor: 'rgba(244,63,94,0.30)', marginLeft: 4 }}>
              ✕ Clear
            </button>
          )}
        </div>
        {/* Search + city — second row */}
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name / designation / dept…"
            style={{ ...inp, flex: 1 }} />
          <div style={{ width: 220 }}>
            <SearchableSelect value={cityFilter} onChange={setCityFilter}
              options={[{ value: '', label: 'All cities' }, ...cities.map(c => ({ value: c, label: c }))]}
              placeholder="All cities" />
          </div>
        </div>
      </div>

      {/* Main table */}
      <div style={{ borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.04)' }}>
              <Th w={220}>Employee</Th>
              <Th w={140}>Designation</Th>
              <Th w={110}>Department</Th>
              <Th w={110}>Group Tag</Th>
              <Th w={160}>Status</Th>
              <Th w={170}>Location</Th>
              <Th w={170}>Active hours</Th>
              <Th w={120}>Roster</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} style={emptyCell}>Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} style={emptyCell}>
                {rows.length === 0
                  ? 'No employees are being monitored. Tick "Monitor on Workforce Live View" on a Group Tag to add its members.'
                  : 'No matches.'}
              </td></tr>
            )}
            {filtered.map(r => <Row key={r.id} r={r} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
function Row({ r }) {
  const bucket = (r.on_leave ? 'leave' : r.on_wfh ? 'wfh' : bucketize(r));
  const statusLabel = (() => {
    if (r.on_leave) return 'On leave';
    if (r.on_wfh && r.presence_status && r.presence_status !== 'Offline')
      return `WFH · ${r.presence_status}`;
    if (r.on_wfh) return 'WFH';
    const a = r.presence_activity || r.presence_status || '—';
    return a.replace(/([A-Z])/g, ' $1').trim();
  })();
  // Dot colour: leave + WFH overlays win first (they're entity-level, not
  // Graph-level); otherwise look up the availability value directly.
  const dotColor = (() => {
    if (r.on_leave) return '#EF9F27';                     // amber
    if (r.on_wfh)   return '#378ADD';                     // blue (WFH borrows the AvailableIdle hue)
    return AVAIL_TO_DOT[r.presence_status] || '#FFFFFF';   // white = unknown
  })();
  const initials = (r.full_name || '').split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase() || '?';
  return (
    <tr style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
      <Td>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Avatar — photo if uploaded, initials bubble as fallback. The
              photo_path is served from /uploads/... behind the same auth as
              the rest of the app. */}
          {r.photo_path ? (
            <img src={r.photo_path} alt={r.full_name}
              onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'inline-flex'; }}
              style={{ width: 30, height: 30, borderRadius: 999, objectFit: 'cover', flexShrink: 0, border: '1px solid rgba(255,255,255,0.10)' }} />
          ) : null}
          <div style={{
            display: r.photo_path ? 'none' : 'inline-flex',
            width: 30, height: 30, borderRadius: 999,
            background: 'rgba(0,224,255,0.10)', border: '1px solid rgba(0,224,255,0.40)',
            color: '#6FE6FF', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700, flexShrink: 0,
          }}>{initials}</div>
          <div>
            <div style={{ fontSize: 13, color: '#E5E7EB', fontWeight: 500 }}>{r.full_name}</div>
            {r.email && <div style={{ fontSize: 11, color: '#64748B' }}>{r.email}</div>}
          </div>
        </div>
      </Td>
      <Td><span style={{ color: '#CBD5E1' }}>{r.designation || '—'}</span></Td>
      <Td><span style={{ color: '#CBD5E1' }}>{r.dept_name || '—'}</span></Td>
      <Td><span style={{ color: '#94A3B8', fontSize: 11.5 }}>{r.group_tag_name || '—'}</span></Td>
      <Td>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: 999, background: dotColor, display: 'inline-block', border: dotColor === '#FFFFFF' ? '1px solid rgba(255,255,255,0.40)' : 'none' }} />
          <span style={{ color: '#E5E7EB' }}>{statusLabel}</span>
        </span>
      </Td>
      <Td>
        <div style={{ color: '#CBD5E1' }}>
          {r.last_signin_city
            ? `${r.last_signin_city}${r.last_signin_country ? ', ' + r.last_signin_country : ''}`
            : '—'}
        </div>
      </Td>
      <Td><ActiveHoursBar firstLogin={r.first_login_today} lastActivity={r.last_signin_at} /></Td>
      <Td><RosterPill status={r.roster_status} /></Td>
    </tr>
  );
}

// ───────────────────────────────────────────────────────────────────
// Active hours progress bar — single cell that visualises the time
// between today's first login and last activity. Click toggles a
// floating tooltip with the exact timestamps.
//
// Bar is normalised against an 8-hour workday: 100% fill = 8h+ worked.
// Colour scales: amber under 4h (< half day), green at 4-9h (full day),
// blue over 9h (long day — kept calm so it doesn't read as alarm).
function ActiveHoursBar({ firstLogin, lastActivity }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  if (!firstLogin) {
    return <span style={{ color: '#64748B', fontSize: 11.5 }}>—</span>;
  }
  const start = new Date(firstLogin).getTime();
  const end = lastActivity ? new Date(lastActivity).getTime() : Date.now();
  const elapsedMs = Math.max(0, end - start);
  const hours = elapsedMs / 3_600_000;
  const target = 8;          // workday baseline
  const pct = Math.min(100, (hours / target) * 100);

  // Colour by hours bucket
  let fill = '#9DC559';      // green default (4-9h)
  if (hours < 4)      fill = '#EF9F27';      // amber — under 4h
  else if (hours > 9) fill = '#85B7EB';      // blue — overtime

  function toggle() {
    if (!open && ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, left: r.left });
    }
    setOpen(o => !o);
  }
  useEffect(() => {
    if (!open) return;
    const onClick = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Pretty hours label e.g. "6h 45m" or "0h 28m"
  const totalMin = Math.round(elapsedMs / 60_000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  const label = `${h}h ${String(m).padStart(2, '0')}m`;

  return (
    <div ref={ref} onClick={toggle} title="Click for details" style={{ cursor: 'pointer', minWidth: 130 }}>
      <div style={{
        position: 'relative', height: 16, width: '100%',
        borderRadius: 4, overflow: 'hidden',
        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)',
      }}>
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`,
          background: fill, opacity: 0.85,
        }} />
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 10.5, fontWeight: 700, color: '#0B1020', letterSpacing: 0.3,
          mixBlendMode: 'screen',     // keeps text readable on top of the fill
        }}>{label}</div>
      </div>
      {open && pos && createPortal(
        <div style={{
          position: 'fixed', top: pos.top, left: pos.left, zIndex: 9500,
          background: '#0B1020', border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 8, padding: '10px 12px', minWidth: 200,
          boxShadow: '0 12px 28px rgba(0,0,0,0.45)',
        }}>
          <div style={{ fontSize: 10.5, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700, marginBottom: 6 }}>Activity today</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
            <span style={{ color: '#94A3B8' }}>First login</span>
            <span style={{ color: '#E5E7EB', fontWeight: 500 }}>{formatTime(firstLogin)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
            <span style={{ color: '#94A3B8' }}>Last activity</span>
            <span style={{ color: '#E5E7EB', fontWeight: 500 }}>{lastActivity ? formatTime(lastActivity) : '—'}</span>
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 6, paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
            <span style={{ color: '#94A3B8' }}>Active duration</span>
            <span style={{ color: '#E5E7EB', fontWeight: 500 }}>{label}</span>
          </div>
          {lastActivity && (
            <div style={{ marginTop: 6, fontSize: 10.5, color: '#64748B' }}>
              Last activity {timeAgo(lastActivity)} · timestamps from Entra sign-in log
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

/** Render a server timestamp as local-time HH:MM. Returns "—" for null. */
function formatTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  } catch (_) { return '—'; }
}

function RosterPill({ status }) {
  let label, color, bg, border;
  switch (status) {
    case 'compliant':
      label = '✓ on roster'; color = '#9DC559'; bg = 'rgba(99,153,34,0.15)'; border = 'rgba(99,153,34,0.40)'; break;
    case 'non-compliant':
      label = '⚠ off roster'; color = '#FB7185'; bg = 'rgba(244,63,94,0.10)'; border = 'rgba(244,63,94,0.40)'; break;
    case 'off-roster':
      label = '— rest day';   color = '#94A3B8'; bg = 'rgba(148,163,184,0.10)'; border = 'rgba(148,163,184,0.30)'; break;
    default:
      label = '—'; color = '#94A3B8'; bg = 'rgba(148,163,184,0.10)'; border = 'rgba(148,163,184,0.30)';
  }
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 999,
      color, background: bg, border: `1px solid ${border}`, textTransform: 'uppercase', letterSpacing: 0.4,
    }}>{label}</span>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.round(diffMs / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

// ───────────────────────────────────────────────────────────────────
// Color Code legend modal — opens from the small button next to "Refresh now".
function ColorCodeModal({ onClose }) {
  return createPortal(
    <div style={modalBackdrop} onClick={onClose}>
      <div style={modalBody} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ margin: 0, fontSize: 16 }}>Color code</h2>
          <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.04)', color: '#94A3B8', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
        </div>
        <div style={{ fontSize: 12.5, color: '#94A3B8', marginBottom: 12 }}>
          Each row's status dot reflects the value Microsoft Graph returned for that user. Leave + WFH overlays
          are coloured separately (amber + blue) — Teams presence colour applies otherwise.
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.04)' }}>
              <th style={legendTh}>Value</th>
              <th style={legendTh}>Meaning</th>
              <th style={legendTh}>Dot colour</th>
            </tr>
          </thead>
          <tbody>
            {COLOR_CODE.map(c => (
              <tr key={c.value} style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                <td style={legendTd}><strong style={{ color: '#E5E7EB' }}>{c.value}</strong></td>
                <td style={{ ...legendTd, color: '#CBD5E1' }}>{c.meaning}</td>
                <td style={legendTd}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 12, height: 12, borderRadius: 999, background: c.dot, border: c.label === 'White' ? '1px solid rgba(255,255,255,0.40)' : 'none' }} />
                    <span style={{ color: '#CBD5E1' }}>{c.label}</span>
                  </span>
                </td>
              </tr>
            ))}
            <tr style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <td style={legendTd}><strong style={{ color: '#E5E7EB' }}>On leave</strong></td>
              <td style={{ ...legendTd, color: '#CBD5E1' }}>Approved leave covering today</td>
              <td style={legendTd}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: 999, background: '#EF9F27' }} /><span style={{ color: '#CBD5E1' }}>Amber</span></span></td>
            </tr>
            <tr style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <td style={legendTd}><strong style={{ color: '#E5E7EB' }}>WFH</strong></td>
              <td style={{ ...legendTd, color: '#CBD5E1' }}>Approved WFH covering today (overrides presence colour)</td>
              <td style={legendTd}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: 999, background: '#378ADD' }} /><span style={{ color: '#CBD5E1' }}>Blue</span></span></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>,
    document.body
  );
}

const Th = ({ children, w }) => (
  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 10.5, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, width: w }}>{children}</th>
);
const Td = ({ children }) => <td style={{ padding: '10px 12px', verticalAlign: 'middle' }}>{children}</td>;
const emptyCell = { padding: '40px 12px', textAlign: 'center', color: '#64748B', fontSize: 12.5, fontStyle: 'italic' };
const inp = {
  padding: '7px 10px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.10)',
  background: 'rgba(15,23,42,0.6)', color: '#E5E7EB', fontSize: 12.5, outline: 'none',
};
const btnSecondary = {
  padding: '7px 14px', borderRadius: 8, fontSize: 12.5, fontWeight: 600,
  border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.04)', color: '#E5E7EB', cursor: 'pointer',
};
// Small outlined "Color Code" button — a touch smaller than Refresh now, with
// three coloured dots so its purpose is obvious before clicking.
const btnLegend = {
  padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
  border: '1px solid rgba(0,224,255,0.40)', background: 'rgba(0,224,255,0.06)', color: '#6FE6FF', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center',
};
// Filter chip — `on` is the toggled state, `f` provides the colour palette.
function chipStyle(on, f) {
  return {
    padding: '5px 12px', borderRadius: 999, fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
    border: `1px solid ${on ? f.border : 'rgba(255,255,255,0.10)'}`,
    background: on ? f.color : 'transparent',
    color: on ? f.text : '#94A3B8',
  };
}
const groupLabel = {
  fontSize: 10, color: '#64748B', fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', marginRight: 4, minWidth: 92,
};
const modalBackdrop = {
  position: 'fixed', inset: 0, zIndex: 9000,
  background: 'rgba(2, 6, 23, 0.70)', backdropFilter: 'blur(2px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const modalBody = {
  width: 'min(720px, 100%)', maxHeight: '90vh', overflowY: 'auto',
  background: '#0B1020', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 14, padding: 18,
  boxShadow: '0 20px 60px rgba(0,0,0,0.55)',
};
const legendTh = { padding: '8px 10px', textAlign: 'left', fontSize: 10.5, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 };
const legendTd = { padding: '10px', verticalAlign: 'middle' };
